import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { ChevronDown, Flag, Eye, EyeOff, Scissors, Undo2, Plus, Trash2, Calendar, Combine, Lock, Download, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ROLE_BADGE_COLORS } from './constants';
import {
  buildPhaseBoundaries,
  buildMonthBands,
  buildTodayWeek,
  buildProjectProgress,
  buildReleaseMarkers,
  buildConditionOverlays,
  buildComponentBreakResolver,
  splitSegmentsAroundBreaks,
} from './ganttHelpers';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '../ui/context-menu';

// Approx rendered label widths (tuned to match the chip + date line below).
const MS_LABEL_PX = 96;
const GOLIVE_LABEL_PX = 108;
const LANE_PX = 30;           // vertical spacing between staggered lanes
const LANE_PAD_PX = 6;         // horizontal breathing room between neighbour labels
const MS_BAND_PAD_PX = 14;     // space reserved above the first lane for the marker triangle/flag
const COLLAPSE_THRESHOLD = 8;  // auto-collapse milestones on dense timelines

// Same allow-list as the backend (planner.py OVERRIDE_GROUPS) — only these
// groups support block scheduling. Used to decide whether to show split /
// drag affordances on a component bar in edit mode.
const EDIT_OVERRIDE_GROUPS = new Set([
  'Consulting & Delivery',
  'Core Configuration',
  'Extended Configuration',
  'User Experience',
  'Data Migration',
  'Integrations',
]);

export function GanttChart({ schedule, totalWeeks, compareSchedule, planMode, milestones, estimateId, defaultMilestonesCollapsed, defaultMilestonesCollapsedSource, startDate, editable = false, onBlocksChange = null, phaseSchedule = null, releases = null, conditions = null, onWaveTune = null, onMilestoneMove = null, onMilestoneNote = null }) {
  const [expanded, setExpanded] = useState({});
  const laneRef = useRef(null);
  // Wave drag-to-tune: preview state + ref (waves view only)
  const waveDragRef = useRef(null);
  const [waveDrag, setWaveDrag] = useState(null); // { key, mode, delta }
  // Milestone drag-to-move: preview state + ref
  const msDragRef = useRef(null);
  const [msDrag, setMsDrag] = useState(null); // { idx, week }
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);

  const beginWaveTune = (e, gs, comp, mode, laneEl) => {
    if (!onWaveTune || !laneEl) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = laneEl.getBoundingClientRect();
    if (!rect.width) return;
    const d = {
      gs, comp, mode,
      weekPx: rect.width / (totalWeeks || 1),
      startX: e.clientX,
      delta: 0,
      origStart: comp.start_week,
      origDur: comp.end_week - comp.start_week + 1,
    };
    waveDragRef.current = d;
    const key = `${gs.wave_id}|${comp.name}`;
    setWaveDrag({ key, mode, delta: 0 });
    const onMove = (ev) => {
      const dd = waveDragRef.current;
      if (!dd) return;
      let delta = Math.round(((ev.clientX - dd.startX) / dd.weekPx) * 2) / 2; // snap 0.5wk
      if (dd.mode === 'move') delta = Math.max(delta, dd.gs.start_week - dd.origStart); // offset >= 0
      else delta = Math.max(delta, 0.5 - dd.origDur); // duration >= 0.5
      dd.delta = delta;
      setWaveDrag({ key, mode: dd.mode, delta });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const dd = waveDragRef.current;
      waveDragRef.current = null;
      setWaveDrag(null);
      if (!dd || !dd.delta) return;
      const r2 = (v) => Math.round(v * 100) / 100;
      const newStart = dd.mode === 'move' ? dd.origStart + dd.delta : dd.origStart;
      const newDur = dd.mode === 'resize' ? dd.origDur + dd.delta : dd.origDur;
      onWaveTune({
        componentName: dd.comp.name,
        waveId: dd.gs.wave_id,
        offset_weeks: r2(Math.max(0, newStart - dd.gs.start_week)),
        duration_weeks: r2(Math.max(0.5, newDur)),
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  // Pending drag state: { compName, blockIdx, originStartWeek, pointerStartX, laneWidthPx }
  const dragRef = useRef(null);
  const [, setDragTick] = useState(0);
  const [pendingBlocks, setPendingBlocks] = useState(null); // { [compName]: [{start_week, weeks}] } in-flight optimistic state
  const scrollRef = useRef(null);
  const [laneWidth, setLaneWidth] = useState(0);
  const msCount = (milestones || []).length;
  // Resolve collapsed state with a 3-step precedence:
  //   1. Per-estimate sessionStorage override (user manually toggled).
  //   2. User/Org default from `defaultMilestonesCollapsed` prop (bool) — when set.
  //   3. Auto-threshold: collapse when >= COLLAPSE_THRESHOLD milestones.
  const sessionKey = estimateId ? `gantt-milestones-collapsed::${estimateId}` : null;
  const readSessionOverride = () => {
    try {
      if (sessionKey && typeof window !== 'undefined' && window.sessionStorage) {
        const stored = window.sessionStorage.getItem(sessionKey);
        if (stored === '1') return true;
        if (stored === '0') return false;
      }
    } catch (e) { /* ignore */ }
    return null;
  };
  const resolveInitialCollapsed = () => {
    const override = readSessionOverride();
    if (override !== null) return override;
    if (typeof defaultMilestonesCollapsed === 'boolean') return defaultMilestonesCollapsed;
    return msCount >= COLLAPSE_THRESHOLD;
  };
  const [milestonesCollapsed, setMilestonesCollapsedState] = useState(resolveInitialCollapsed);
  const [hasSessionOverride, setHasSessionOverride] = useState(() => readSessionOverride() !== null);
  // Persist manual toggles for the lifetime of the browser session per
  // estimate so a PM who always presents collapsed doesn't re-collapse
  // on every revisit. Clears naturally when the tab closes.
  const setMilestonesCollapsed = (next) => {
    setMilestonesCollapsedState(prev => {
      const val = typeof next === 'function' ? next(prev) : next;
      try {
        if (sessionKey && typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(sessionKey, val ? '1' : '0');
        }
      } catch (e) { /* ignore */ }
      return val;
    });
    setHasSessionOverride(true);
  };
  // Re-resolve when the estimate changes (new session key) or a new
  // default flows in from the server (e.g., user just toggled their
  // Profile → Planner Defaults preference).
  useLayoutEffect(() => {
    setMilestonesCollapsedState(resolveInitialCollapsed());
    setHasSessionOverride(readSessionOverride() !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, defaultMilestonesCollapsed]);

  useLayoutEffect(() => {
    if (!laneRef.current) return;
    const update = () => setLaneWidth(laneRef.current ? laneRef.current.offsetWidth : 0);
    update();
    const RO = typeof window !== 'undefined' ? window.ResizeObserver : null;
    if (!RO) { window.addEventListener('resize', update); return () => window.removeEventListener('resize', update); }
    const ro = new RO(update);
    ro.observe(laneRef.current);
    return () => ro.disconnect();
  }, [milestones, totalWeeks, milestonesCollapsed]);

  const hasSchedule = !!(schedule && schedule.length);
  const maxWeek = hasSchedule ? Math.max(totalWeeks, compareSchedule ? Math.max(...compareSchedule.map(g => g.end_week)) : 0) : 0;

  // ── Drag-to-edit helpers ─────────────────────────────────────────────
  // pendingBlocks holds optimistic in-flight block edits keyed by component
  // name; the bar renderer reads from this first, then falls back to the
  // server-provided planner_blocks_override.
  const getEffectiveBlocks = useCallback((comp) => {
    if (pendingBlocks && pendingBlocks[comp.name]) return pendingBlocks[comp.name];
    if (Array.isArray(comp.planner_blocks_override) && comp.planner_blocks_override.length > 0) {
      return comp.planner_blocks_override;
    }
    return null;
  }, [pendingBlocks]);

  const splitComponent = useCallback((comp) => {
    if (!editable || !onBlocksChange) return;
    const start = comp.start_week || 1;
    const end = comp.end_week || start;
    const total = Math.max(2, end - start + 1);
    const block1Weeks = Math.max(1, Math.floor(total / 2));
    const block2Weeks = Math.max(1, total - block1Weeks - 1);
    const block2Start = Math.min(maxWeek, start + block1Weeks + 1);
    const next = [
      { start_week: start, weeks: block1Weeks },
      { start_week: block2Start, weeks: block2Weeks },
    ];
    setPendingBlocks(prev => ({ ...(prev || {}), [comp.name]: next }));
    onBlocksChange(comp.name, next);
  }, [editable, onBlocksChange, maxWeek]);

  const clearComponentBlocks = useCallback((comp) => {
    if (!editable || !onBlocksChange) return;
    setPendingBlocks(prev => {
      const next = { ...(prev || {}) };
      delete next[comp.name];
      return next;
    });
    onBlocksChange(comp.name, null);
  }, [editable, onBlocksChange]);

  // Context-menu mutations on a single block within a multi-block component.
  // All paths route through onBlocksChange so persistence + refetch happens
  // through the standard PATCH endpoint.
  const updateOneBlock = useCallback((comp, currentBlocks, blockIdx, patch) => {
    if (!editable || !onBlocksChange) return;
    const next = currentBlocks.map(b => ({ ...b }));
    next[blockIdx] = { ...next[blockIdx], ...patch };
    setPendingBlocks(prev => ({ ...(prev || {}), [comp.name]: next }));
    onBlocksChange(comp.name, next);
  }, [editable, onBlocksChange]);

  const removeOneBlock = useCallback((comp, currentBlocks, blockIdx) => {
    if (!editable || !onBlocksChange) return;
    const next = currentBlocks.filter((_, i) => i !== blockIdx);
    if (next.length === 0) {
      // No blocks left — clear the override entirely.
      clearComponentBlocks(comp);
      return;
    }
    setPendingBlocks(prev => ({ ...(prev || {}), [comp.name]: next }));
    onBlocksChange(comp.name, next);
  }, [editable, onBlocksChange, clearComponentBlocks]);

  const addBlockAfter = useCallback((comp, currentBlocks, blockIdx) => {
    if (!editable || !onBlocksChange) return;
    const ref = currentBlocks[blockIdx] || {};
    const refStart = ref.start_week || 1;
    const refWeeks = ref.weeks || 1;
    const proposedStart = Math.min(maxWeek, refStart + refWeeks + 1);
    const proposedWeeks = Math.max(1, Math.min(2, maxWeek - proposedStart + 1));
    const newBlock = { start_week: proposedStart, weeks: proposedWeeks };
    const next = [...currentBlocks];
    next.splice(blockIdx + 1, 0, newBlock);
    setPendingBlocks(prev => ({ ...(prev || {}), [comp.name]: next }));
    onBlocksChange(comp.name, next);
  }, [editable, onBlocksChange, maxWeek]);

  const promptSetStart = useCallback((comp, currentBlocks, blockIdx) => {
    if (typeof window === 'undefined') return;
    const cur = currentBlocks[blockIdx]?.start_week || 1;
    const raw = window.prompt(`Start week for block ${blockIdx + 1} (1–${maxWeek}):`, String(cur));
    if (raw === null) return;
    const v = parseInt(raw);
    if (!Number.isFinite(v) || v < 1 || v > maxWeek) return;
    updateOneBlock(comp, currentBlocks, blockIdx, { start_week: v });
  }, [maxWeek, updateOneBlock]);

  const promptSetDuration = useCallback((comp, currentBlocks, blockIdx) => {
    if (typeof window === 'undefined') return;
    const cur = currentBlocks[blockIdx]?.weeks || 1;
    const start = currentBlocks[blockIdx]?.start_week || 1;
    const raw = window.prompt(`Duration in weeks for block ${blockIdx + 1} (1–${Math.max(1, maxWeek - start + 1)}):`, String(cur));
    if (raw === null) return;
    const v = parseInt(raw);
    if (!Number.isFinite(v) || v < 1) return;
    updateOneBlock(comp, currentBlocks, blockIdx, { weeks: Math.min(v, maxWeek - start + 1) });
  }, [maxWeek, updateOneBlock]);

  // Snap a block to a project phase boundary. Sets start_week = phase.start_week
  // and weeks = phase.end_week - phase.start_week + 1, clamped to maxWeek.
  // Used by the "Lock to phase: <Phase>" context-menu items so PMs can express
  // "this component pauses for UAT" with a single click.
  const lockBlockToPhase = useCallback((comp, currentBlocks, blockIdx, phase) => {
    if (!editable || !onBlocksChange || !phase) return;
    const start = Math.max(1, Math.min(maxWeek, parseInt(phase.start_week) || 1));
    const end = Math.max(start, Math.min(maxWeek, parseInt(phase.end_week) || start));
    const weeks = end - start + 1;
    updateOneBlock(comp, currentBlocks, blockIdx, { start_week: start, weeks });
  }, [editable, onBlocksChange, maxWeek, updateOneBlock]);

  // Build a clean, deduped list of phase boundaries usable in the context menu.
  // Phases come from the planner's phase_schedule which is always available
  // (passed independently of the active gantt view).
  const phaseBoundaries = React.useMemo(
    () => buildPhaseBoundaries(phaseSchedule),
    [phaseSchedule],
  );

  const startDrag = useCallback((evt, comp, blockIdx, currentBlocks) => {
    if (!editable || !onBlocksChange) return;
    evt.stopPropagation();
    evt.preventDefault();
    const lane = evt.currentTarget.closest('[data-gantt-lane]');
    if (!lane) return;
    const rect = lane.getBoundingClientRect();
    dragRef.current = {
      compName: comp.name,
      blockIdx,
      originBlocks: currentBlocks.map(b => ({ ...b })),
      pointerStartX: evt.clientX,
      laneLeft: rect.left,
      laneWidth: rect.width,
      moved: false,
    };
    setDragTick(t => t + 1);
  }, [editable, onBlocksChange]);

  // Window-level pointermove + pointerup so dragging keeps tracking even if
  // the cursor leaves the bar element.
  useEffect(() => {
    if (!editable) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const pxPerWeek = d.laneWidth / Math.max(1, maxWeek);
      const dx = e.clientX - d.pointerStartX;
      const weekDelta = Math.round(dx / pxPerWeek);
      if (weekDelta === 0) return;
      d.moved = true;
      const next = d.originBlocks.map(b => ({ ...b }));
      const target = next[d.blockIdx];
      if (!target) return;
      const proposed = Math.max(1, Math.min(maxWeek, (d.originBlocks[d.blockIdx].start_week || 1) + weekDelta));
      target.start_week = proposed;
      target.weeks = Math.max(1, Math.min(target.weeks || 1, maxWeek - proposed + 1));
      setPendingBlocks(prev => ({ ...(prev || {}), [d.compName]: next }));
    };
    const onUp = () => {
      const d = dragRef.current;
      if (!d) return;
      if (d.moved && onBlocksChange) {
        const committed = (pendingBlocks && pendingBlocks[d.compName]) || d.originBlocks;
        onBlocksChange(d.compName, committed);
      }
      dragRef.current = null;
      setDragTick(t => t + 1);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [editable, onBlocksChange, maxWeek, pendingBlocks]);

  // When the server-side schedule updates (parent re-fetched), drop any
  // matching pending entries so we read the fresh truth from `schedule`.
  useEffect(() => {
    if (!pendingBlocks) return;
    let changed = false;
    const next = { ...pendingBlocks };
    (schedule || []).forEach(g => (g.components || []).forEach(c => {
      const fromServer = Array.isArray(c.planner_blocks_override) ? c.planner_blocks_override : null;
      const pend = next[c.name];
      if (!pend || !fromServer) return;
      if (fromServer.length === pend.length && fromServer.every((b, i) => b.start_week === pend[i].start_week && b.weeks === pend[i].weeks)) {
        delete next[c.name];
        changed = true;
      }
    }));
    if (changed) setPendingBlocks(Object.keys(next).length === 0 ? null : next);
  }, [schedule, pendingBlocks]);

  if (!hasSchedule) return null;
  const weekLabels = Array.from({ length: maxWeek }, (_, i) => `W${i + 1}`);

  // Month-band segments: when startDate is provided, group consecutive weeks that
  // fall within the same calendar month into a single header cell. Gives stakeholders
  // a calendar context above the weekly grid without hiding the weeks.
  const monthBands = buildMonthBands(startDate, maxWeek);
  const compareByGroup = {};
  if (compareSchedule) compareSchedule.forEach(g => { compareByGroup[g.group] = g; });
  const expandableGroups = schedule.filter(g => g.components && g.components.length > 0);

  // v261 — Wave colour coding (waves view): stable palette per wave, in
  // schedule order, shared by the legend chips, row dots and group bars.
  const WAVE_COLORS = ['#4ABFCF', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#3B82F6'];
  const waveColorById = {};
  {
    let wk = 0;
    schedule.forEach(g => {
      if (g.wave_id && !(g.wave_id in waveColorById)) {
        waveColorById[g.wave_id] = WAVE_COLORS[wk % WAVE_COLORS.length];
        wk += 1;
      }
    });
  }
  const waveLegend = schedule.filter(g => g.wave_id);
  const allExpanded = expandableGroups.length > 0 && expandableGroups.every(g => expanded[g.group]);
  const toggle = (grp) => setExpanded(prev => ({ ...prev, [grp]: !prev[grp] }));
  const toggleAll = () => { const next = {}; expandableGroups.forEach(g => { next[g.group] = !allExpanded; }); setExpanded(next); };
  const showDates = planMode === 'goals' || planMode === 'absolute' || planMode === 'conditions';
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  // "Today" marker — vertical rule at the week that contains today's date,
  // only when startDate is known and today falls within the project horizon.
  const todayWeek = buildTodayWeek(startDate, maxWeek);
  // Project progress chip — visible whenever a startDate exists, even before
  // the project starts (gives PMs an at-a-glance "starts in N weeks" cue) or
  // after it ends ("Completed"). During the project span, mirrors the Today
  // marker with `Tn / Nw · Mw left`.
  const projectProgress = buildProjectProgress(startDate, maxWeek);

  // Go-Live milestone — rendered specially (flag marker + dashed rule).
  const goLive = (milestones || []).find(m => (m?.name || '').toLowerCase().startsWith('go live'));
  const goLiveLabel = goLive
    ? (goLive.date ? `Go-Live — ${fmtDate(goLive.date)}` : `Go-Live — W${goLive.week}`)
    : null;

  // Public Cloud release markers — only those that fall within the project
  // horizon get a vertical rule + colored chip, so PMs can avoid scheduling
  // go-lives inside maintenance windows or land just before a release.
  const releaseMarkers = buildReleaseMarkers(startDate, releases, maxWeek);
  const RELEASE_COLORS = {
    test: { rule: 'rgba(8, 145, 178, 0.55)', chip: 'bg-cyan-600' },
    prod: { rule: 'rgba(16, 185, 129, 0.65)', chip: 'bg-emerald-600' },
    maintenance: { rule: 'rgba(217, 119, 6, 0.55)', chip: 'bg-amber-600' },
  };

  // ── Project Conditions overlays (Batch C) ──────────────────────────
  // Convert ISO date ranges in `conditions.breaks` / `conditions.blackouts`
  // to {start_week, end_week} so they render as absolute-positioned bars
  // in each schedule row's lane. Skip silently when startDate is unknown.
  const conditionOverlays = buildConditionOverlays(conditions, startDate, maxWeek);

  // ── Per-component break assignment (Phase 1) ──────────────────────
  // For each break with applies_to='all' or 'specific', resolve which
  // component names honor it. Used to (a) render warning chips on the
  // bar label and (b) split the bar visually around the break weeks.
  const { getBreaksForComponent } = buildComponentBreakResolver(conditions, conditionOverlays);

  // Build a contextual tooltip for the Go-Live flag — "51 weeks from
  // kickoff · 3 weeks Hypercare slack" — so consultants can sense the
  // schedule without leaving the chart.
  const goLiveTooltip = (() => {
    if (!goLive) return null;
    const lines = [
      `Go-Live · Week ${goLive.week}${goLive.date ? ' · ' + fmtDate(goLive.date) : ''}`,
    ];
    const projectStart = (milestones || []).find(m => (m?.name || '').toLowerCase().startsWith('project start'));
    if (projectStart && Number.isFinite(projectStart.week) && Number.isFinite(goLive.week)) {
      const wks = goLive.week - projectStart.week;
      lines.push(`${wks} week${wks === 1 ? '' : 's'} from project start`);
    }
    const hypercare = (milestones || []).find(m => (m?.name || '').toLowerCase().startsWith('hypercare'));
    if (hypercare && Number.isFinite(hypercare.week) && Number.isFinite(goLive.week)) {
      const slack = hypercare.week - goLive.week;
      lines.push(`${slack} week${slack === 1 ? '' : 's'} hypercare runway`);
    }
    return lines.join(' · ');
  })();

  // ── Lane assignment (anti-overlap) ───────────────────────────────────────
  // Every milestone — Go-Live included — participates. Labels are packed
  // into the fewest possible horizontal lanes; labels that would collide are
  // pushed down one lane. Falls back to a reasonable estimated lane width
  // before the ResizeObserver fires on the first render. When the Milestones
  // band is collapsed, only Go-Live is laid out — the hidden milestones are
  // summarised by a "+N more" chip adjacent to the flag.
  const effectiveLaneWidth = laneWidth || Math.max(600, maxWeek * 40) - 192 - 80;
  const msWithLane = [];
  let laneCount = 0;
  const hiddenMilestones = [];
  if (milestones && milestones.length && effectiveLaneWidth > 0) {
    const items = milestones
      .map((m, i) => ({ ...m, _idx: i }))
      .filter(m => typeof m.week === 'number' && m.week > 0)
      .sort((a, b) => (a.week - b.week) || (a._idx - b._idx));
    const laneRightEdge = []; // px right-edge of last placed label in each lane
    items.forEach((m) => {
      const isGoLive = (m.name || '').toLowerCase().startsWith('go live');
      if (milestonesCollapsed && !isGoLive && !m.is_wave) { hiddenMilestones.push(m); return; }
      const centerPct = ((Math.min(m.week, maxWeek) - 0.5) / maxWeek) * 100;
      const centerPx = (centerPct / 100) * effectiveLaneWidth;
      const half = (isGoLive ? GOLIVE_LABEL_PX : MS_LABEL_PX) / 2;
      const leftPx = centerPx - half;
      const rightPx = centerPx + half;
      let lane = 0;
      while (lane < laneRightEdge.length && laneRightEdge[lane] + LANE_PAD_PX > leftPx) lane++;
      if (lane === laneRightEdge.length) laneRightEdge.push(rightPx);
      else laneRightEdge[lane] = rightPx;
      msWithLane.push({ m, lane, leftPct: centerPct, isGoLive });
    });
    laneCount = laneRightEdge.length;
  }
  const hiddenCount = hiddenMilestones.length;
  const bandHeightPx = Math.max(1, laneCount) * LANE_PX + MS_BAND_PAD_PX;

  // Scroll container ref — used by the "Jump to today" button to snap
  // the viewport to the current-week marker on long timelines.
  const jumpToToday = () => {
    if (!scrollRef.current || !todayWeek) return;
    const inner = scrollRef.current.firstElementChild;
    const minWidth = inner ? inner.scrollWidth : 0;
    if (!minWidth) return;
    // Mirror the same column math used by the today-rule: label col (192) + inset (4)
    const LABEL_COL = 192, INSET = 4, EFFORT_COL = 80;
    const usable = minWidth - LABEL_COL - EFFORT_COL - INSET;
    const x = LABEL_COL + INSET + ((todayWeek - 0.5) / maxWeek) * usable;
    const container = scrollRef.current;
    const target = Math.max(0, x - container.clientWidth / 2);
    container.scrollTo({ left: target, behavior: 'smooth' });
  };

  // Milestone drag-to-move — horizontal drag on the milestone band snaps to
  // 0.5-week and persists via onMilestoneMove(name, week, prevWeek, wasOverridden).
  const beginMilestoneDrag = (e, m) => {
    if (!onMilestoneMove || !laneRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = laneRef.current.getBoundingClientRect();
    if (!rect.width) return;
    const weekPx = rect.width / Math.max(1, maxWeek);
    const d = { name: m.name, idx: m._idx, startX: e.clientX, origWeek: m.week, week: m.week, overridden: !!m.overridden };
    msDragRef.current = d;
    setMsDrag({ idx: m._idx, week: m.week });
    const onMove = (ev) => {
      const dd = msDragRef.current;
      if (!dd) return;
      const delta = Math.round(((ev.clientX - dd.startX) / weekPx) * 2) / 2;
      dd.week = Math.min(maxWeek, Math.max(1, dd.origWeek + delta));
      setMsDrag({ idx: dd.idx, week: dd.week });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const dd = msDragRef.current;
      msDragRef.current = null;
      setMsDrag(null);
      if (!dd || dd.week === dd.origWeek) return;
      onMilestoneMove(dd.name, dd.week, dd.origWeek, dd.overridden);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Milestone note — right-click any milestone to attach/edit a short note.
  const promptMilestoneNote = (m) => {
    if (!onMilestoneNote) return;
    const current = m.note || '';
    const raw = window.prompt(`Note for "${m.name}" (blank to remove):`, current);
    if (raw === null) return;
    const next = raw.trim().slice(0, 280);
    if (next === current.trim()) return;
    onMilestoneNote(m.name, next || null);
  };

  // Shared capture for the one-page waves snapshot exports (PDF + DOCX):
  // expands every wave, waits for layout, renders the full-width chart.
  const captureWavesCanvas = async () => {
    const next = {};
    expandableGroups.forEach(g => { next[g.group] = true; });
    setExpanded(next);
    await new Promise(r => setTimeout(r, 450));
    const el = scrollRef.current && scrollRef.current.firstElementChild;
    if (!el) throw new Error('chart not ready');
    const html2canvas = (await import('html2canvas')).default;
    return html2canvas(el, {
      scale: 1.5,
      backgroundColor: '#ffffff',
      width: el.scrollWidth,
      height: el.scrollHeight,
      windowWidth: el.scrollWidth + 200,
    });
  };

  // One-page PDF snapshot of the (fully expanded) Waves Gantt.
  const exportWavesPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    const prevExpanded = expanded;
    try {
      const canvas = await captureWavesCanvas();
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 8;
      pdf.setFontSize(13);
      pdf.setTextColor(15, 32, 64);
      pdf.text('Deployment Waves — Gantt', margin, 12);
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }), pw - margin, 12, { align: 'right' });
      const availW = pw - margin * 2;
      const availH = ph - 16 - margin;
      const ratio = Math.min(availW / canvas.width, availH / canvas.height);
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.85), 'JPEG', margin, 16, canvas.width * ratio, canvas.height * ratio);
      pdf.save(`deployment-waves-gantt-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Waves Gantt exported as a one-page PDF');
    } catch (err) {
      toast.error('Could not export the waves Gantt');
    } finally {
      setExpanded(prevExpanded);
      setExportingPdf(false);
    }
  };

  // Matching one-page Word export — same snapshot wrapped in a landscape DOCX.
  const exportWavesDocx = async () => {
    if (exportingDocx) return;
    setExportingDocx(true);
    const prevExpanded = expanded;
    try {
      const canvas = await captureWavesCanvas();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/planner/waves-gantt-docx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_base64: canvas.toDataURL('image/jpeg', 0.85) }),
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deployment-waves-gantt-${new Date().toISOString().slice(0, 10)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Waves Gantt exported as a Word document');
    } catch (err) {
      toast.error('Could not export the waves Gantt as Word');
    } finally {
      setExpanded(prevExpanded);
      setExportingDocx(false);
    }
  };

  return (
    <div className="relative">
      {/* Project-status chip + Jump-to-today button. Rendered as a small
          floating toolbar in the top-left of the chart area itself (not in
          the negative-top region) so it never collides with the Card's
          "Implementation Phase / Component Group" view toggle in the header. */}
      {(projectProgress || todayWeek) && (
        <div
          className="flex items-center gap-1.5 mb-2"
          data-testid="gantt-toolbar"
        >
          {projectProgress && (
            <div data-testid="gantt-progress-chip" data-state={projectProgress.state}>
              {projectProgress.state === 'before' && (
                <span
                  className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1"
                  title={`Project starts ${projectProgress.startLabel}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                  Starts in {projectProgress.weeksUntilStart}w
                </span>
              )}
              {projectProgress.state === 'during' && (
                <span
                  className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
                  title={`Currently in week ${projectProgress.currentWeek} of ${projectProgress.totalWeeks} (${projectProgress.pctComplete}% complete)`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  T{projectProgress.currentWeek} / {projectProgress.totalWeeks}w
                  <span className="text-slate-400 mx-0.5">·</span>
                  <span className="text-slate-600 font-medium">{projectProgress.weeksRemaining}w left</span>
                </span>
              )}
              {projectProgress.state === 'after' && (
                <span
                  className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                  title={`Project span ended ${projectProgress.endedAgoWeeks}w ago`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Completed
                </span>
              )}
            </div>
          )}
          {todayWeek && (
            <button
              type="button"
              onClick={jumpToToday}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 rounded px-2 py-1 border border-red-200/70 bg-white/90 transition-colors"
              title={`Scroll to current week (W${todayWeek})`}
              data-testid="gantt-jump-to-today-btn"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              Jump to today
            </button>
          )}
        </div>
      )}
      <div ref={scrollRef} className="overflow-x-auto">
      <div
        style={{ minWidth: Math.max(600, maxWeek * 40) }}
        className="relative"
      >
        {/* Dashed vertical guideline at the Go-Live boundary — sits behind
            all bars. Offset accounts for the 192px label column + a 4px
            breathing-room inset. */}
        {goLive && (
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: `calc(192px + 4px + ${(Math.min(goLive.week, maxWeek) - 0.5) / maxWeek} * (100% - 192px - 80px - 4px))`,
              borderLeft: '1.5px dashed rgba(74, 191, 207, 0.65)',
              zIndex: 1,
            }}
            data-testid="gantt-golive-rule"
          />
        )}
        {/* Past-weeks shading — faint grey wash from W1 to todayWeek-1 so
            the Gantt visually communicates "this is what's already happened".
            Only renders during the project span (todayWeek defined). Sits
            beneath all bars/markers (zIndex 0). */}
        {todayWeek && todayWeek > 1 && (
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: '192px',
              width: `calc(4px + ${(todayWeek - 1) / maxWeek} * (100% - 192px - 80px - 4px))`,
              background: 'linear-gradient(to right, rgba(15, 32, 64, 0.06) 0%, rgba(15, 32, 64, 0.04) 100%)',
              zIndex: 0,
            }}
            data-testid="gantt-past-weeks-shading"
          />
        )}
        {todayWeek && (
          <>
            <div
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                left: `calc(192px + 4px + ${(todayWeek - 0.5) / maxWeek} * (100% - 192px - 80px - 4px))`,
                borderLeft: '1.5px solid rgba(239, 68, 68, 0.75)',
                zIndex: 1,
              }}
              data-testid="gantt-today-rule"
            />
            <div
              className="absolute pointer-events-none z-20"
              style={{
                left: `calc(192px + 4px + ${(todayWeek - 0.5) / maxWeek} * (100% - 192px - 80px - 4px))`,
                top: '-6px',
                transform: 'translateX(-50%)',
              }}
              data-testid="gantt-today-chip"
              title={`Today — Week ${todayWeek} (${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`}
            >
              <span className="inline-block text-[9px] font-bold text-white bg-red-500 rounded px-1.5 py-0.5 leading-none whitespace-nowrap shadow-sm">
                Today
              </span>
            </div>
          </>
        )}
        {/* Public Cloud release markers — vertical rule spans full chart;
            chips live in their own dedicated row above the milestone band
            so they no longer collide with milestone labels.
            v258.3 — added an invisible hit-area on each rule so hovering
            anywhere on the dashed line surfaces a tooltip identifying the
            release (label · date · project week). */}
        {releaseMarkers.map((evt, i) => {
          const colors = RELEASE_COLORS[evt.release_type] || RELEASE_COLORS.test;
          const tip = `${evt.label || evt.quarter || 'Release'} — ${evt.date} (project W${evt.week})`;
          return (
            <React.Fragment key={`rel-rule-${evt.id || i}`}>
              <div
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: `calc(192px + 4px + ${(evt.week - 0.5) / maxWeek} * (100% - 192px - 80px - 4px))`,
                  borderLeft: `1.25px dashed ${colors.rule}`,
                  zIndex: 1,
                }}
                data-testid={`gantt-release-rule-${evt.release_type}-${i}`}
              />
              {/* Invisible 8px-wide hit area centered on the rule for
                  hover tooltips. Doesn't intercept clicks on bars
                  (z-1, no background, just a title attribute). */}
              <div
                className="absolute top-0 bottom-0 z-10"
                style={{
                  left: `calc(192px + 4px + ${(evt.week - 0.5) / maxWeek} * (100% - 192px - 80px - 4px) - 4px)`,
                  width: '8px',
                  cursor: 'help',
                }}
                title={tip}
                data-testid={`gantt-release-hit-${evt.release_type}-${i}`}
              />
            </React.Fragment>
          );
        })}
        {releaseMarkers.length > 0 && (
          <div
            className="flex items-center"
            data-testid="gantt-release-lane"
          >
            <div className="w-48 shrink-0 pl-2 pr-2 flex items-center gap-1">
              <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Releases</span>
            </div>
            <div className="flex-1 relative h-6">
              {releaseMarkers.map((evt, i) => {
                const colors = RELEASE_COLORS[evt.release_type] || RELEASE_COLORS.test;
                const tip = `${evt.label || evt.quarter || 'Release'} — ${evt.date} (project W${evt.week})`;
                // Distinguish test / prod / maintenance via a 1-char suffix
                // on the chip so two releases in the same quarter no longer
                // render identically (v258.3).
                const rtSuffix = (evt.release_type || '').toLowerCase() === 'prod' ? '·P'
                  : (evt.release_type || '').toLowerCase() === 'test' ? '·T'
                  : (evt.release_type || '').toLowerCase() === 'maintenance' ? '·M'
                  : '';
                return (
                  <div
                    key={`rel-chip-${evt.id || i}`}
                    className="absolute z-20"
                    style={{
                      // Match the chart math but offset for the 192px label column
                      // we're now sitting *inside* of (so subtract that from the left).
                      left: `calc(4px + ${(evt.week - 0.5) / maxWeek} * (100% - 80px - 4px))`,
                      top: '4px',
                      transform: 'translateX(-50%)',
                    }}
                    title={tip}
                    data-testid={`gantt-release-chip-${evt.release_type}-${i}`}
                  >
                    <span className={`inline-block text-[9px] font-medium text-white ${colors.chip} rounded px-1 py-0.5 leading-none whitespace-nowrap shadow-sm`}>
                      {((evt.quarter || '').replace(/^Q\d+\s*/, '') || (evt.release_type || '').slice(0, 1).toUpperCase())}{rtSuffix}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {milestones && milestones.length > 0 && (
          <div
            className="flex items-start mb-3"
            data-testid="gantt-milestones"
          >
            <div className="w-48 shrink-0 pl-2 pr-2 pt-1 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-[#0F2040]">Milestones</span>
                {msCount >= 2 && (
                  <button
                    type="button"
                    onClick={() => setMilestonesCollapsed(v => !v)}
                    className="flex items-center gap-1 text-[9px] font-medium text-slate-500 hover:text-[#0F2040] transition-colors rounded px-1 py-0.5 hover:bg-slate-100"
                    data-testid="gantt-milestones-toggle"
                    title={milestonesCollapsed ? 'Show all milestones' : 'Collapse milestones (show only Go-Live + summary)'}
                  >
                    {milestonesCollapsed ? (
                      <><Eye className="w-3 h-3" /><span>Show all</span></>
                    ) : (
                      <><EyeOff className="w-3 h-3" /><span>Collapse</span></>
                    )}
                  </button>
                )}
              </div>
              {/* Helper footnote — only shown when the opening state is
                  driven by a saved preference (user or org). When the user
                  has manually overridden via the session toggle, we show
                  a tiny "Reset to default" link so the preference isn't
                  lost in the UX. Auto-threshold opens stay silent. */}
              {(defaultMilestonesCollapsedSource === 'user' || defaultMilestonesCollapsedSource === 'org') && (
                hasSessionOverride ? (
                  <div className="flex items-center gap-1 text-[8.5px] text-slate-400 leading-tight" data-testid="gantt-milestones-default-hint">
                    <span>Overriding {defaultMilestonesCollapsedSource === 'user' ? 'your' : 'org'} default</span>
                    <span>·</span>
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          if (sessionKey && typeof window !== 'undefined' && window.sessionStorage) {
                            window.sessionStorage.removeItem(sessionKey);
                          }
                        } catch (e) { /* ignore */ }
                        setHasSessionOverride(false);
                        if (typeof defaultMilestonesCollapsed === 'boolean') {
                          setMilestonesCollapsedState(defaultMilestonesCollapsed);
                        }
                      }}
                      className="text-[#4ABFCF] hover:text-[#0F2040] font-medium underline underline-offset-2 decoration-dotted"
                      data-testid="gantt-milestones-reset-default"
                    >
                      Reset
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-[8.5px] text-slate-400 leading-tight" data-testid="gantt-milestones-default-hint">
                    <span>
                      Applied default: {milestonesCollapsed ? 'Collapsed' : 'Expanded'} ·
                    </span>
                    {defaultMilestonesCollapsedSource === 'user' ? (
                      <Link
                        to="/profile"
                        className="text-[#4ABFCF] hover:text-[#0F2040] font-medium underline underline-offset-2 decoration-dotted"
                        title="Open Profile → Planner Defaults"
                      >
                        your profile
                      </Link>
                    ) : (
                      <span className="text-slate-500 font-medium">org default</span>
                    )}
                  </div>
                )
              )}
            </div>
            <div
              ref={laneRef}
              className="flex-1 relative"
              style={{ height: `${bandHeightPx}px` }}
            >
              {msWithLane.map(({ m, lane, leftPct, isGoLive }, mi) => {
                const topPx = MS_BAND_PAD_PX + lane * LANE_PX - 2;
                const shortLabel = m.date ? fmtDate(m.date) : `W${m.week}`;
                if (isGoLive) {
                  // Anchor the pill on the dashed Go-Live rule (its
                  // pre-existing position) and float the flag in
                  // immediately to the LEFT of the pill via absolute
                  // positioning, so the rule stays right where it
                  // always was and the flag no longer collides with
                  // milestone labels in the band above.
                  return (
                    <div
                      key={`golive-${mi}`}
                      className="absolute -translate-x-1/2 z-20"
                      style={{ left: `${leftPct}%`, top: `${topPx}px` }}
                      onContextMenu={onMilestoneNote ? (e) => { e.preventDefault(); promptMilestoneNote(m); } : undefined}
                      data-testid="gantt-golive-flag"
                      title={(goLiveTooltip || goLiveLabel) + (m.note ? ` · Note: ${m.note}` : '') + (onMilestoneNote ? ' · right-click to add/edit note' : '')}
                    >
                      <span
                        className="relative inline-block text-[9px] font-bold text-[#0F2040] bg-[#4ABFCF]/15 border border-[#4ABFCF]/40 rounded px-1 leading-none whitespace-nowrap"
                      >
                        <Flag
                          className="absolute right-full top-1/2 -translate-y-1/2 mr-1 w-4 h-4 text-[#0F2040]"
                          strokeWidth={2.5}
                          fill="#4ABFCF"
                        />
                        {goLiveLabel}
                      </span>
                    </div>
                  );
                }
                // Abbreviate long milestone names for always-visible chip.
                const rawName = m.name || '';
                const nameShort = rawName.length > 22
                  ? rawName.replace(/\bComplete\b/gi, '').replace(/\s+/g, ' ').trim().slice(0, 20) + '…'
                  : rawName;
                const msAnchor = /^project (start|end)$/i.test(m.name || '');
                const msDraggable = !!onMilestoneMove && !m.is_wave && !msAnchor;
                const msDragging = !!(msDrag && msDrag.idx === m._idx);
                const fmtW = (v) => Math.round(v * 10) / 10;
                const dispWeek = msDragging ? msDrag.week : m.week;
                const dispLeftPct = msDragging ? ((Math.min(dispWeek, maxWeek) - 0.5) / maxWeek) * 100 : leftPct;
                const msTitle = `${m.name}${m.date && !msDragging ? ' • ' + fmtDate(m.date) : ' • W' + fmtW(dispWeek)}`
                  + (m.note ? ` • Note: ${m.note}` : '')
                  + (m.overridden ? ' • manually placed — double-click to reset' : '')
                  + (msDraggable ? ' • drag to move (0.5-wk snap)' : '')
                  + (onMilestoneNote ? ' • right-click to add/edit note' : '');
                return (
                  <div key={`${m.name}-${mi}`}
                    className={`absolute -translate-x-1/2 group/ms flex flex-col items-center ${msDraggable ? 'cursor-grab active:cursor-grabbing' : ''} ${msDragging ? 'z-30' : ''}`}
                    style={{ left: `${dispLeftPct}%`, top: `${topPx}px`, maxWidth: '100px' }}
                    onPointerDown={msDraggable ? (e) => beginMilestoneDrag(e, m) : undefined}
                    onDoubleClick={msDraggable && m.overridden ? () => onMilestoneMove(m.name, null, m.week, true) : undefined}
                    onContextMenu={onMilestoneNote ? (e) => { e.preventDefault(); promptMilestoneNote(m); } : undefined}
                    data-testid={m.is_wave ? `gantt-wave-milestone-${m._idx}` : `gantt-milestone-${m._idx}`}
                    title={msTitle}>
                    <div className={`w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent ${m.is_wave ? 'border-t-[#4ABFCF]' : 'border-t-[#F59E0B]'}`} />
                    <div className="mt-0.5 flex flex-col items-center leading-tight">
                      <span className={`text-[8px] font-semibold text-[#0F2040] rounded px-1 whitespace-nowrap border ${m.is_wave ? 'bg-[#4ABFCF]/10 border-[#4ABFCF]/50' : 'bg-amber-50 border-amber-300'} ${m.overridden ? 'ring-1 ring-cyan-400/70' : ''}`}>
                        {nameShort}
                        {m.note && <StickyNote className="inline-block w-2 h-2 ml-0.5 -mt-0.5 text-sky-600" data-testid={`gantt-milestone-note-icon-${m._idx}`} />}
                      </span>
                      <span className={`text-[7.5px] font-medium mt-[1px] ${m.is_wave ? 'text-[#0F6B78]' : 'text-amber-700'}`}>
                        {msDragging ? `W${fmtW(dispWeek)}` : shortLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
              {milestonesCollapsed && hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setMilestonesCollapsed(false)}
                  className="absolute top-0 right-0 group/more flex items-center gap-1 text-[9px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5 hover:bg-amber-100 hover:border-amber-400 transition-colors z-10 shadow-sm"
                  data-testid="gantt-milestones-more"
                  title={
                    hiddenMilestones
                      .map(m => `${m.name} — ${m.date ? fmtDate(m.date) : `W${m.week}`}`)
                      .join('\n')
                  }
                >
                  <span className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-amber-600" />
                  <span>+{hiddenCount} milestone{hiddenCount === 1 ? '' : 's'}</span>
                  <div
                    className="pointer-events-none absolute right-0 top-full mt-1 w-56 max-h-48 overflow-y-auto rounded-md border border-amber-300 bg-white shadow-lg p-2 text-left opacity-0 group-hover/more:opacity-100 transition-opacity z-30"
                  >
                    <div className="text-[9px] font-bold text-[#0F2040] mb-1 uppercase tracking-wide">Hidden milestones</div>
                    <ul className="space-y-0.5">
                      {hiddenMilestones.map((m, hi) => (
                        <li key={`h-${m._idx ?? hi}`} className="flex items-start gap-1.5 text-[10px] text-slate-700">
                          <span className="mt-[3px] w-0 h-0 border-l-[3px] border-r-[3px] border-t-[4px] border-l-transparent border-r-transparent border-t-[#F59E0B] shrink-0" />
                          <span className="font-medium text-[#0F2040] truncate">{m.name}</span>
                          <span className="ml-auto text-amber-700 shrink-0">{m.date ? fmtDate(m.date) : `W${m.week}`}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-[9px] text-slate-500 mt-1.5 italic">Click to expand all</div>
                  </div>
                </button>
              )}
            </div>
            <div className="w-20 shrink-0" />
          </div>
        )}
        {waveLegend.length > 0 && (
          <div className="flex items-center mb-1.5">
            <div className="w-48 shrink-0" />
            <div className="flex-1 flex items-center gap-3 flex-wrap py-0.5" data-testid="gantt-wave-legend">
              {waveLegend.map((g, li) => (
                <span key={g.wave_id} className="inline-flex items-center gap-1 text-[9px] text-slate-500" data-testid={`gantt-wave-legend-chip-${li}`}>
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: waveColorById[g.wave_id] }} />
                  <span className="font-semibold text-[#0F2040]">{g.group}</span>
                  <span>W{g.start_week}–W{g.start_week + g.duration_weeks - 1}</span>
                </span>
              ))}
              <button
                type="button"
                onClick={exportWavesPdf}
                disabled={exportingPdf}
                data-html2canvas-ignore="true"
                className="ml-auto shrink-0 inline-flex items-center gap-1 text-[9px] font-semibold text-slate-500 hover:text-[#0F2040] border border-slate-200 hover:border-[#4ABFCF] bg-white rounded px-1.5 py-0.5 transition-colors disabled:opacity-60"
                title="Export the fully expanded waves Gantt as a one-page PDF"
                data-testid="gantt-waves-export-pdf-btn"
              >
                <Download className="w-3 h-3" />
                {exportingPdf ? 'Exporting…' : 'PDF'}
              </button>
              <button
                type="button"
                onClick={exportWavesDocx}
                disabled={exportingDocx}
                data-html2canvas-ignore="true"
                className="shrink-0 inline-flex items-center gap-1 text-[9px] font-semibold text-slate-500 hover:text-[#0F2040] border border-slate-200 hover:border-[#4ABFCF] bg-white rounded px-1.5 py-0.5 transition-colors disabled:opacity-60"
                title="Export the fully expanded waves Gantt as a one-page Word document"
                data-testid="gantt-waves-export-docx-btn"
              >
                <Download className="w-3 h-3" />
                {exportingDocx ? 'Exporting…' : 'DOCX'}
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center mb-2">
          <div className="w-48 shrink-0 flex items-center">
            {expandableGroups.length > 0 && (
              <button onClick={toggleAll} className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-[#0F2040] transition-colors" data-testid="gantt-toggle-all">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${allExpanded ? 'rotate-0' : '-rotate-90'}`} />
                <span>{allExpanded ? 'Collapse All' : 'Expand All'}</span>
              </button>
            )}
          </div>
          <div className="flex-1 flex flex-col">
            {monthBands && (
              <div className="flex border-b border-slate-100 mb-0.5" data-testid="gantt-month-header">
                {monthBands.map((m, idx) => (
                  <div
                    key={`${m.key}-${idx}`}
                    className="text-[9px] font-semibold text-slate-500 text-center uppercase tracking-wider border-r border-slate-100 last:border-r-0 py-0.5"
                    style={{ flex: m.span }}
                    title={m.label}
                  >
                    {m.span >= 2 ? m.label : '·'}
                  </div>
                ))}
              </div>
            )}
            <div className="flex-1 flex">
              {weekLabels.map(w => (<div key={w} className="flex-1 text-[9px] text-slate-400 text-center">{w}</div>))}
            </div>
          </div>
          <div className="w-20 shrink-0 text-[9px] text-slate-400 text-right pr-2">Effort</div>
        </div>
        {schedule.map((gs, i) => {
          const isOpen = expanded[gs.group];
          const hasComponents = gs.components && gs.components.length > 0;
          return (
            <div key={gs.group} data-testid={`gantt-row-${i}`}>
              <div className="flex items-center group/row hover:bg-slate-50/50 rounded transition-colors" data-testid={`gantt-group-${i}`}>
                <button className="w-48 shrink-0 flex items-center gap-1.5 text-left pr-2 py-1.5"
                  onClick={() => hasComponents && toggle(gs.group)} data-testid={`gantt-toggle-${i}`}>
                  {hasComponents ? <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`} /> : <span className="w-3.5" />}
                  {gs.wave_id && waveColorById[gs.wave_id] && (
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: waveColorById[gs.wave_id] }} data-testid={`gantt-wave-color-dot-${i}`} />
                  )}
                  <span className="text-[11px] font-semibold text-[#0F2040] truncate">{gs.group}</span>
                  {gs.status && (
                    <span
                      className={`text-[8px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${
                        gs.status === 'launched' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : gs.status === 'in_flight' ? 'bg-sky-50 text-sky-700 border-sky-200'
                            : 'bg-slate-50 text-slate-500 border-slate-200'
                      }`}
                      data-testid={`gantt-wave-status-${i}`}
                    >
                      {gs.status === 'launched' ? 'Launched' : gs.status === 'in_flight' ? 'In flight' : 'Planned'}
                    </span>
                  )}
                  {gs.slip_days != null && (
                    <span
                      className={`text-[8px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${
                        gs.slip_days > 0 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}
                      title={gs.actual_golive_date ? `Went live ${gs.actual_golive_date} vs planned ${gs.end_date || ''}` : undefined}
                      data-testid={`gantt-wave-slip-${i}`}
                    >
                      {gs.slip_days === 0 ? 'On time' : gs.slip_days > 0 ? `${gs.slip_days}d late` : `${Math.abs(gs.slip_days)}d early`}
                    </span>
                  )}
                </button>
                <div className="flex-1 relative h-7">
                  {/* Project Conditions overlays (Batch C) — render below bars (z-0). */}
                  {conditionOverlays.breaks.map((b) => {
                    const left = ((b.start_week - 1) / maxWeek) * 100;
                    const width = ((b.end_week - b.start_week + 1) / maxWeek) * 100;
                    return (
                      <div
                        key={`break-${b.id}`}
                        className="absolute top-0 bottom-0 z-0 pointer-events-none border-l border-r border-amber-300/70"
                        style={{
                          left: `${left}%`, width: `${width}%`,
                          backgroundImage: 'repeating-linear-gradient(45deg, rgba(245,158,11,0.18) 0px, rgba(245,158,11,0.18) 4px, rgba(245,158,11,0.05) 4px, rgba(245,158,11,0.05) 8px)',
                        }}
                        title={`Break: ${b.label || 'paused work'} — W${b.start_week}-W${b.end_week}`}
                        data-testid={`gantt-break-overlay-${b.id}`}
                      />
                    );
                  })}
                  {conditionOverlays.blackouts.map((b) => {
                    const left = ((b.start_week - 1) / maxWeek) * 100;
                    const width = ((b.end_week - b.start_week + 1) / maxWeek) * 100;
                    return (
                      <div
                        key={`blackout-${b.id}`}
                        className="absolute top-0 bottom-0 z-0 pointer-events-none border-l-2 border-r-2 border-rose-400/80"
                        style={{
                          left: `${left}%`, width: `${width}%`,
                          backgroundColor: 'rgba(244, 63, 94, 0.12)',
                        }}
                        title={`Blackout: ${b.label || 'no-deploy window'} — W${b.start_week}-W${b.end_week}`}
                        data-testid={`gantt-blackout-overlay-${b.id}`}
                      />
                    );
                  })}
                  <div className="absolute top-0 h-3 rounded-sm bg-[#0F2040] flex items-center z-10 overflow-hidden"
                    style={{
                      left: `${((gs.start_week - 1) / maxWeek) * 100}%`,
                      width: `${(gs.duration_weeks / maxWeek) * 100}%`,
                      ...(gs.wave_id && waveColorById[gs.wave_id] ? { background: waveColorById[gs.wave_id] } : {}),
                    }}>
                    {/* Per-bar progress fill — lighter inset showing how much of
                        this group's weeks have already elapsed. Renders only when
                        today is during the project span and overlaps the bar. */}
                    {todayWeek && todayWeek > gs.start_week - 1 && (
                      <div
                        className="absolute inset-y-0 left-0 pointer-events-none"
                        style={{
                          width: `${Math.min(100, Math.max(0, ((todayWeek - (gs.start_week - 1)) / gs.duration_weeks) * 100))}%`,
                          background: 'linear-gradient(to right, rgba(74, 191, 207, 0.45), rgba(74, 191, 207, 0.25))',
                        }}
                        title={`${Math.min(100, Math.max(0, Math.round(((todayWeek - (gs.start_week - 1)) / gs.duration_weeks) * 100)))}% elapsed`}
                        data-testid={`gantt-bar-progress-${gs.group}`}
                      />
                    )}
                    <span className="relative z-10 text-[8px] text-white pl-1 whitespace-nowrap">
                      {showDates && gs.start_date ? `${fmtDate(gs.start_date)} – ${fmtDate(gs.end_date)}` : `${gs.duration_weeks}w`}
                    </span>
                  </div>
                  {compareSchedule && compareByGroup[gs.group] && (
                    <div className="absolute top-3.5 h-3 rounded-sm bg-teal-500 flex items-center z-10"
                      style={{ left: `${((compareByGroup[gs.group].start_week - 1) / maxWeek) * 100}%`, width: `${(compareByGroup[gs.group].duration_weeks / maxWeek) * 100}%` }}>
                      <span className="text-[8px] text-white pl-1 whitespace-nowrap">{compareByGroup[gs.group].duration_weeks}w</span>
                    </div>
                  )}
                </div>
                <div className="w-20 shrink-0 text-right pr-2">
                  <span className="text-[11px] font-semibold text-[#0F2040]">{gs.total_effort} hrs</span>
                </div>
              </div>
              {isOpen && hasComponents && (
                <div className="border-l-2 border-slate-200 ml-5 mb-2" data-testid={`gantt-components-${i}`}>
                  {gs.components.map((comp, ci) => {
                    const duration = comp.end_week - comp.start_week + 1;
                    const badgeColor = ROLE_BADGE_COLORS[comp.role] || 'bg-slate-100 text-slate-600';
                    const isHypercare = comp.name === 'Hypercare Support' && comp.hypercare_scope;
                    const hc = comp.hypercare_scope || {};
                    const shiftedTo = comp.planner_start_week_override;
                    const weeksOverride = comp.planner_weeks_override;
                    const truncatedFrom = comp.planner_weeks_truncated_from;
                    const blocksOverride = getEffectiveBlocks(comp);
                    const canEditBlocks = editable && !!onBlocksChange && EDIT_OVERRIDE_GROUPS.has(gs.group);
                    const compSlugForTestId = comp.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    const hcTitle = isHypercare
                      ? `Hypercare: ${hc.weeks}w × ${hc.fte} FTE × ${hc.hours_per_day}h/day × ${hc.days_per_week}d/wk = ${comp.effort} hrs`
                      : comp.name;
                    // Phase-1 break overlap detection (visual only). When a
                    // honored break overlaps this component's [start_week,
                    // end_week] range we (a) flag the row with an amber chip
                    // and (b) render the bar as multiple segments split
                    // around the break weeks.
                    const compBreaks = blocksOverride ? [] : getBreaksForComponent(comp.name)
                      .filter(br => br.end_week >= comp.start_week && br.start_week <= comp.end_week);
                    const breakSegments = compBreaks.length
                      ? splitSegmentsAroundBreaks(comp.start_week, comp.end_week, compBreaks)
                      : null;
                    return (
                      <div key={`${comp.name}-${ci}`} className="flex items-center hover:bg-slate-50/60 rounded transition-colors group/row" data-testid={`gantt-comp-${i}-${ci}`}>
                        <div className="w-48 shrink-0 flex items-center gap-2 pl-3 pr-2 py-1">
                          {comp.is_stage ? (
                            comp.is_phase ? (
                              <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-black text-white uppercase tracking-wide" data-testid={`gantt-phase-chip-${i}-${ci}`}>Phase</span>
                            ) : (
                              <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-slate-200/80 text-slate-500 uppercase tracking-wide" data-testid={`gantt-stage-chip-${i}-${ci}`}>Stage</span>
                            )
                          ) : (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{comp.role}</span>
                          )}
                          <span className={`text-[10px] truncate ${comp.is_phase ? 'text-black font-extrabold uppercase tracking-wide' : comp.is_stage ? 'text-slate-400 italic' : 'text-slate-600'}`} title={hcTitle}>{comp.name}</span>
                          {blocksOverride && blocksOverride.length > 0 && (
                            <span
                              className="text-[8px] font-semibold px-1 py-0.5 rounded shrink-0 bg-purple-100 text-purple-800 ring-1 ring-purple-200/80"
                              title={`Split schedule: ${blocksOverride.map(b => `W${b.start_week}-W${b.start_week + b.weeks - 1}`).join(' + ')}`}
                              data-testid={`gantt-blocks-${i}-${ci}`}
                            >
                              {blocksOverride.length} blocks
                            </span>
                          )}
                          {!blocksOverride && (shiftedTo || weeksOverride) && (
                            <span
                              className={`text-[8px] font-semibold px-1 py-0.5 rounded shrink-0 ${
                                truncatedFrom
                                  ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300/80'
                                  : 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-200/80'
                              }`}
                              title={
                                truncatedFrom
                                  ? `Weeks override trimmed from ${truncatedFrom} to ${(comp.end_week - comp.start_week + 1)} to fit project horizon${shiftedTo ? ` — starts W${shiftedTo}` : ''}`
                                  : `User-overridden schedule${shiftedTo ? ` — starts W${shiftedTo}` : ''}${weeksOverride ? ` — runs ${weeksOverride} week${weeksOverride === 1 ? '' : 's'}` : ''}`
                              }
                              data-testid={`gantt-shifted-${i}-${ci}`}
                            >
                              {shiftedTo ? `↪W${shiftedTo}` : ''}{shiftedTo && weeksOverride ? ' · ' : ''}{weeksOverride ? `${weeksOverride}w` : ''}{truncatedFrom ? '!' : ''}
                            </span>
                          )}
                          {canEditBlocks && !blocksOverride && (
                            <button
                              type="button"
                              onClick={() => splitComponent(comp)}
                              className="ml-auto p-0.5 text-slate-300 hover:text-purple-600 opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0"
                              title="Split this component into two blocks (drag the second block to adjust)"
                              data-testid={`gantt-split-btn-${compSlugForTestId}`}
                            >
                              <Scissors className="w-3 h-3" />
                            </button>
                          )}
                          {canEditBlocks && blocksOverride && (
                            <button
                              type="button"
                              onClick={() => clearComponentBlocks(comp)}
                              className="ml-auto p-0.5 text-slate-300 hover:text-amber-600 transition-colors shrink-0"
                              title="Merge blocks back into a single bar"
                              data-testid={`gantt-merge-btn-${compSlugForTestId}`}
                            >
                              <Undo2 className="w-3 h-3" />
                            </button>
                          )}
                          {compBreaks.length > 0 && (
                            <span
                              className="text-[8px] font-semibold px-1 py-0.5 rounded shrink-0 bg-amber-100 text-amber-800 ring-1 ring-amber-300/80"
                              title={compBreaks.map(b => `Pauses for ${b.label} (W${b.start_week}-W${b.end_week})`).join(' · ')}
                              data-testid={`gantt-break-affected-${compSlugForTestId}`}
                            >
                              ⚠ {compBreaks.length === 1 ? compBreaks[0].label : `${compBreaks.length} breaks`}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 relative h-4" data-gantt-lane={canEditBlocks ? '1' : undefined}>
                          {/* Project Conditions overlays (Batch C) — replicated per component row. */}
                          {conditionOverlays.breaks.map((b) => {
                            const left = ((b.start_week - 1) / maxWeek) * 100;
                            const width = ((b.end_week - b.start_week + 1) / maxWeek) * 100;
                            return (
                              <div
                                key={`break-comp-${b.id}`}
                                className="absolute top-0 bottom-0 z-0 pointer-events-none"
                                style={{
                                  left: `${left}%`, width: `${width}%`,
                                  backgroundImage: 'repeating-linear-gradient(45deg, rgba(245,158,11,0.18) 0px, rgba(245,158,11,0.18) 4px, rgba(245,158,11,0.05) 4px, rgba(245,158,11,0.05) 8px)',
                                }}
                              />
                            );
                          })}
                          {conditionOverlays.blackouts.map((b) => {
                            const left = ((b.start_week - 1) / maxWeek) * 100;
                            const width = ((b.end_week - b.start_week + 1) / maxWeek) * 100;
                            return (
                              <div
                                key={`blackout-comp-${b.id}`}
                                className="absolute top-0 bottom-0 z-0 pointer-events-none"
                                style={{
                                  left: `${left}%`, width: `${width}%`,
                                  backgroundColor: 'rgba(244, 63, 94, 0.10)',
                                }}
                              />
                            );
                          })}
                          {blocksOverride && blocksOverride.length > 0 ? (
                            blocksOverride.map((blk, bi) => {
                              const blkStart = Math.max(1, parseInt(blk.start_week) || 1);
                              const blkWeeks = Math.max(1, parseInt(blk.weeks) || 1);
                              const blkDuration = Math.min(blkWeeks, maxWeek - blkStart + 1);
                              if (blkDuration <= 0) return null;
                              const blockBar = (
                                <div
                                  className="absolute top-0.5 h-2.5 rounded-sm ring-1 ring-purple-400/60"
                                  style={{
                                    left: `${((blkStart - 1) / maxWeek) * 100}%`,
                                    width: `${Math.max((blkDuration / maxWeek) * 100, 1)}%`,
                                    backgroundColor: 'rgba(124, 58, 237, 0.30)',
                                  }}
                                  title={`Block ${bi + 1}: W${blkStart}–W${blkStart + blkDuration - 1}${canEditBlocks ? ' · drag, or right-click for more actions' : ''}`}
                                  data-testid={`gantt-block-${i}-${ci}-${bi}`}
                                >
                                  <span className="text-[7px] text-[#0F2040] pl-1 whitespace-nowrap font-medium pointer-events-none">
                                    W{blkStart}{blkDuration > 1 ? `-W${blkStart + blkDuration - 1}` : ''}
                                  </span>
                                  {canEditBlocks && (
                                    <div
                                      role="slider"
                                      tabIndex={0}
                                      onPointerDown={(e) => startDrag(e, comp, bi, blocksOverride)}
                                      onKeyDown={(e) => {
                                        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                                        e.preventDefault();
                                        const delta = e.key === 'ArrowLeft' ? -1 : 1;
                                        const next = blocksOverride.map(b => ({ ...b }));
                                        const target = next[bi];
                                        const proposed = Math.max(1, Math.min(maxWeek, (target.start_week || 1) + delta));
                                        target.start_week = proposed;
                                        target.weeks = Math.max(1, Math.min(target.weeks || 1, maxWeek - proposed + 1));
                                        setPendingBlocks(prev => ({ ...(prev || {}), [comp.name]: next }));
                                        onBlocksChange(comp.name, next);
                                      }}
                                      className="absolute -left-0.5 top-0 h-2.5 w-1.5 cursor-ew-resize bg-purple-700 rounded-sm hover:bg-purple-900 active:bg-purple-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                      title="Drag to move this block (or focus + ←/→)"
                                      data-testid={`gantt-block-handle-${compSlugForTestId}-${bi}`}
                                    />
                                  )}
                                </div>
                              );
                              if (!canEditBlocks) {
                                return <React.Fragment key={`blk-${bi}`}>{blockBar}</React.Fragment>;
                              }
                              return (
                                <ContextMenu key={`blk-${bi}`}>
                                  <ContextMenuTrigger asChild>{blockBar}</ContextMenuTrigger>
                                  <ContextMenuContent className="w-56" data-testid={`gantt-block-menu-${compSlugForTestId}-${bi}`}>
                                    <ContextMenuItem
                                      onSelect={() => promptSetStart(comp, blocksOverride, bi)}
                                      data-testid={`gantt-block-menu-set-start-${compSlugForTestId}-${bi}`}
                                    >
                                      <Calendar className="w-3.5 h-3.5 mr-2" />
                                      Set start week...
                                    </ContextMenuItem>
                                    <ContextMenuItem
                                      onSelect={() => promptSetDuration(comp, blocksOverride, bi)}
                                      data-testid={`gantt-block-menu-set-duration-${compSlugForTestId}-${bi}`}
                                    >
                                      <Calendar className="w-3.5 h-3.5 mr-2" />
                                      Set duration...
                                    </ContextMenuItem>
                                    {phaseBoundaries.length > 0 && (
                                      <ContextMenuSub>
                                        <ContextMenuSubTrigger
                                          data-testid={`gantt-block-menu-lock-phase-trigger-${compSlugForTestId}-${bi}`}
                                        >
                                          <Lock className="w-3.5 h-3.5 mr-2" />
                                          Lock to phase boundary
                                        </ContextMenuSubTrigger>
                                        <ContextMenuSubContent className="w-56">
                                          {phaseBoundaries.map((p) => {
                                            const phaseSlug = p.phase.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                                            return (
                                              <ContextMenuItem
                                                key={`${p.phase}-${p.start_week}`}
                                                onSelect={() => lockBlockToPhase(comp, blocksOverride, bi, p)}
                                                data-testid={`gantt-block-menu-lock-${phaseSlug}-${compSlugForTestId}-${bi}`}
                                              >
                                                <span className="font-medium">{p.phase}</span>
                                                <span className="ml-auto text-[10px] text-slate-400">W{p.start_week}–W{p.end_week}</span>
                                              </ContextMenuItem>
                                            );
                                          })}
                                        </ContextMenuSubContent>
                                      </ContextMenuSub>
                                    )}
                                    <ContextMenuSeparator />
                                    <ContextMenuItem
                                      onSelect={() => addBlockAfter(comp, blocksOverride, bi)}
                                      disabled={blocksOverride.length >= 6}
                                      data-testid={`gantt-block-menu-add-after-${compSlugForTestId}-${bi}`}
                                    >
                                      <Plus className="w-3.5 h-3.5 mr-2" />
                                      Add another block after this
                                    </ContextMenuItem>
                                    {blocksOverride.length > 1 && (
                                      <ContextMenuItem
                                        onSelect={() => clearComponentBlocks(comp)}
                                        data-testid={`gantt-block-menu-merge-all-${compSlugForTestId}-${bi}`}
                                      >
                                        <Combine className="w-3.5 h-3.5 mr-2" />
                                        Merge all blocks back
                                      </ContextMenuItem>
                                    )}
                                    <ContextMenuSeparator />
                                    <ContextMenuItem
                                      onSelect={() => removeOneBlock(comp, blocksOverride, bi)}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`gantt-block-menu-remove-${compSlugForTestId}-${bi}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                                      Remove this block
                                    </ContextMenuItem>
                                  </ContextMenuContent>
                                </ContextMenu>
                              );
                            })
                          ) : breakSegments && breakSegments.length > 0 ? (
                            // Phase-1: render bar split into segments around honored breaks.
                            // Each segment is rendered as a discrete bar; the gap between
                            // segments visually communicates that the work is paused.
                            breakSegments.map((seg, si) => {
                              const [segStart, segEnd] = seg;
                              const segDuration = segEnd - segStart + 1;
                              return (
                                <div
                                  key={`segm-${si}`}
                                  className={`absolute top-0.5 h-2.5 rounded-sm flex items-center ${isHypercare ? 'ring-1 ring-orange-400/60' : 'ring-1 ring-amber-300/40'}`}
                                  style={{
                                    left: `${((segStart - 1) / maxWeek) * 100}%`,
                                    width: `${Math.max((segDuration / maxWeek) * 100, 1)}%`,
                                    backgroundColor: isHypercare ? 'rgba(217, 119, 6, 0.35)' : 'rgba(15, 32, 64, 0.25)',
                                  }}
                                  title={`${comp.name} — W${segStart}${segEnd !== segStart ? `-W${segEnd}` : ''} (paused around: ${compBreaks.map(b => b.label).join(', ')})`}
                                  data-testid={`gantt-break-segment-${compSlugForTestId}-${si}`}
                                >
                                  {si === 0 && (
                                    <span className="text-[7px] text-[#0F2040] pl-1 whitespace-nowrap font-medium">
                                      W{segStart}{segEnd !== segStart ? `-W${segEnd}` : ''}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (() => {
                            const waveTunable = !!(onWaveTune && gs.wave_id && !comp.is_stage);
                            const wdKey = `${gs.wave_id}|${comp.name}`;
                            let dispStart = comp.start_week;
                            let dispDur = duration;
                            if (waveDrag && waveDrag.key === wdKey) {
                              if (waveDrag.mode === 'move') dispStart = comp.start_week + waveDrag.delta;
                              else dispDur = duration + waveDrag.delta;
                            }
                            const rw = (v) => Math.round(v * 10) / 10;
                            const widthPct = Math.max((dispDur / maxWeek) * 100, 1);
                            const endW = rw(dispStart + dispDur - 1);
                            const weeksTip = `starts W${rw(dispStart)}, for ${rw(dispDur)} wk${rw(dispDur) === 1 ? '' : 's'}, ending W${endW}`;
                            return (
                            <div
                              className={`absolute top-0.5 h-2.5 rounded-sm flex items-center ${isHypercare ? 'ring-1 ring-orange-400/60' : ''} ${waveTunable ? 'cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-[#4ABFCF]' : ''}`}
                              style={{
                                left: `${((dispStart - 1) / maxWeek) * 100}%`,
                                width: `${widthPct}%`,
                                backgroundColor: comp.is_phase ? '#000000' : (isHypercare ? 'rgba(217, 119, 6, 0.35)' : 'rgba(15, 32, 64, 0.25)'),
                              }}
                              onPointerDown={waveTunable ? (e) => beginWaveTune(e, gs, comp, 'move', e.currentTarget.parentElement) : undefined}
                              title={waveTunable
                                ? `${comp.name} — ${weeksTip} · drag to move, drag right edge to resize (0.5-wk snap)`
                                : (hcTitle ? `${hcTitle} · ${weeksTip}` : `${comp.name} — ${weeksTip}`)}
                              data-testid={waveTunable ? `gantt-wave-bar-${i}-${ci}` : (isHypercare ? 'gantt-hypercare-bar' : undefined)}>
                              <span className={`text-[7px] pl-1 whitespace-nowrap pointer-events-none ${comp.is_phase ? 'text-white font-bold' : 'text-[#0F2040] font-medium'}`}>
                                {showDates && comp.start_date && !(waveDrag && waveDrag.key === wdKey)
                                  ? `${fmtDate(comp.start_date)}`
                                  : `W${rw(dispStart)}${endW !== rw(dispStart) ? `-W${endW}` : ''}`}
                                {widthPct >= 5 ? ` · ${rw(dispDur)}w` : ''}
                                {isHypercare ? ` • ${hc.fte} FTE` : ''}
                              </span>
                              {waveTunable && (
                                <div
                                  onPointerDown={(e) => beginWaveTune(e, gs, comp, 'resize', e.currentTarget.parentElement.parentElement)}
                                  className="absolute right-0 top-0 h-2.5 w-1.5 cursor-ew-resize bg-[#4ABFCF] rounded-r-sm opacity-0 group-hover/row:opacity-100 hover:opacity-100 transition-opacity"
                                  title="Drag to change duration (fractional weeks)"
                                  data-testid={`gantt-wave-resize-${i}-${ci}`}
                                />
                              )}
                            </div>
                            );
                          })()}
                        </div>
                        <div className="w-20 shrink-0 text-right pr-2"><span className="text-[10px] text-slate-500">{comp.effort} hrs</span></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {compareSchedule && (
          <div className="flex gap-4 mt-3 ml-48">
            <div className="flex items-center gap-1.5"><div className="w-3 h-2.5 rounded-sm bg-[#0F2040]" /><span className="text-[10px] text-slate-600">Base</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-2.5 rounded-sm bg-teal-500" /><span className="text-[10px] text-slate-600">What-If</span></div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
